import { useEffect, useState } from 'react';
import { Paper, Stack, Typography, Grid, CardMedia, Box } from '@mui/material';
import { PieChart, Pie, Cell } from 'recharts';
import EMBLEM_YEAR_1 from '../../static/emblem/Continuous_Year_1.png';
import EMBLEM_YEAR_2 from '../../static/emblem/Continuous_Year_2.png';
import EMBLEM_YEAR_ALL from '../../static/emblem/Continuous_Year_All.png';

export function MemberContinuousInfo(props) {
  const [showMember, setShowMember] = useState(false);
  const [emblemConfigs, setEmblemConfigs] = useState([]);
 
  useEffect(() => {
    if (props?.data?.transactions) {
      setShowMember(true);
      updateEmblemConfigs(props.data.transactions);
    }
  }, [props]);

  const calculateMonthsInRange = (transactions, startDate, endDate) => {
  // Use 15th of the month for stable timezone-agnostic comparisons
  const start = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), 15));
  const end = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), 15));
  
  const uniqueMonths = new Set();
  
  transactions.forEach(transaction => {
    const transactionDate = new Date(transaction.valid_thru);
    // Use 15th of the transaction month for comparison
    const normalizedDate = new Date(Date.UTC(
      transactionDate.getFullYear(),
      transactionDate.getMonth(),
      15
    ));
    
    if (normalizedDate >= start && normalizedDate <= end) {
      const monthYear = `${transactionDate.getFullYear()}-${transactionDate.getMonth()}`;
      uniqueMonths.add(monthYear);
    }
  });
  
  return uniqueMonths.size;
};

const calculateTotalMonthsInRange = (startDate, endDate) => {
  // Simple month count (+1 to include both start and end months)
  return (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
         (endDate.getMonth() - startDate.getMonth()) + 1;
};

const updateEmblemConfigs = (transactions) => {
  const currentYear = 2025; // Fixed to 2025 as per requirements
  const currentMonth = 5;   // June (0-based)
  
  // Year 1: 2024 July to 2025 June (12 months)
  const year1Start = new Date(Date.UTC(2024, 6, 15));  // July 15, 2024
  const year1End = new Date(Date.UTC(2025, 5, 15));    // June 15, 2025
  const year1Count = calculateMonthsInRange(transactions, year1Start, year1End);
  const year1Total = 12; // Fixed 12 months for year1
  const year1Completed = Math.round((year1Count / year1Total) * 100);
  
  // Year 2: 2023 July to 2025 June (24 months)
  const year2Start = new Date(Date.UTC(2023, 6, 15));  // July 15, 2023
  const year2End = year1End;                           // June 15, 2025
  const year2Count = calculateMonthsInRange(transactions, year2Start, year2End);
  const year2Total = 24; // Fixed 24 months for year2
  const year2Completed = Math.round((year2Count / year2Total) * 100);
  
  // All Years: 2021 Dec to 2025 June
  const allStart = new Date(Date.UTC(2021, 11, 15));   // Dec 15, 2021
  const allEnd = new Date(Date.UTC(2025, 5, 15));      // June 15, 2025
  const allCount = calculateMonthsInRange(transactions, allStart, allEnd);
  const allTotal = calculateTotalMonthsInRange(allStart, allEnd);
  const allCompleted = Math.round((allCount / allTotal) * 100);
  
  setEmblemConfigs([
    {
      image: EMBLEM_YEAR_1,
      alt: "1 Year",
      count: year1Count,
      completed: year1Completed,
      dateRange: "2024 JUL - 2025 JUN",
      colors: {
        id: "color1",
        start: "#B94B22",
        end: "#53220F"
      }
    },
    {
      image: EMBLEM_YEAR_2,
      alt: "2 Years",
      count: year2Count,
      completed: year2Completed,
      dateRange: "2023 JUL - 2025 JUN",
      colors: {
        id: "color2",
        start: "#B1B8B8",
        end: "#535358"
      }
    },
    {
      image: EMBLEM_YEAR_ALL,
      alt: "All Years",
      count: allCount,
      completed: allCompleted,
      dateRange: "2021 DEC - 2025 JUN",
      colors: {
        id: "color3",
        start: "#F6CF43",
        end: "#854F14"
      }
    }
  ]);
 }
  const EmblemCard = ({ image, alt, count, completedPercentage, colors, dateRange }) => {
    const pieData = [
      { name: 'Completed', value: completedPercentage },
      { name: 'Remaining', value: 100 - completedPercentage }
    ];

    return (
      <Stack sx={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: "center",
        gap: 1
      }}>
        <CardMedia
          component="img"
          image={image}
          alt={alt}
          sx={{ width: 120, height: 120 }}
        />
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          background: `${colors.end}`,
          borderRadius: '7px',
          padding: '2px 10px'
        }}>
          <Typography variant="caption" sx={{ color: '#ffffff', textAlign: 'center' }}>
            {dateRange}
          </Typography>
        </Box>
        <Box sx={{ 
          width: 120, 
          height: 120,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <PieChart width={120} height={120}>
            <Pie
              data={pieData}
              cx={60}
              cy={60}
              innerRadius={30}
              outerRadius={55}
              paddingAngle={0}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
            >
              <Cell 
                fill={`url(#${colors.id})`} 
                stroke="none"
              />
              <Cell fill="transparent" stroke="none" />
            </Pie>
            <defs>
              <linearGradient id={colors.id} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.start} />
                <stop offset="100%" stopColor={colors.end} />
              </linearGradient>
            </defs>
          </PieChart>
          <Typography 
            sx={{
              position: 'absolute',
              top: '53%',
              left: '53%',
              transform: 'translate(-50%, -50%)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              textAlign: 'center'
            }}
          >
            {count}
          </Typography>
        </Box>
      </Stack>
    );
  };
  return (
    <Paper sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center', 
      borderRadius: '1.5rem'
    }} elevation={3}>
      <Grid container spacing={1} p={2} sx={{ width: '100%' }}>
        {/* <Grid item xs={1} key={8}></Grid> */}
        {showMember && emblemConfigs.map((config, index) => (
          <Grid size={4} key={index}>
            <EmblemCard 
              image={config.image}
              alt={config.alt}
              count={config.count}
              completedPercentage={config.completed}
              colors={config.colors}
              dateRange={config.dateRange}
            />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}